// One of MedSnapp's cast (brand/cast.ts) in the mood of a moment. Works in any look; MascotSlot uses it only under the
// medsnapp look, and the front page may use it anywhere.
//
// The drawing's text is fetched once per page (the same file as the plain <img>, so the bundle never carries it) and put
// inline, where MedSnapp's own face rig (rig.ts) and our puppet (puppet.ts) bring it to life (4 Nov 2026):
//
//   an act      a short choreography for the mood (acts.ts): a dance, a high five, a splat, a faint, a shrug, a yawn,
//               a fly-in with the crown... chosen by the caller (`act`), or the mood's first pick for the character
//   then a loop the mood's gentle loop: joy bounces, a miss sighs, sleep sways, never frozen
//   and life    blinks, a breath, a glance, a fidget; Beat's heart beats
//
// `cue` plays another act later (the lobby's parade); `delay` holds the first act's opening frame for a while. Acts
// paint outside the size × size box (falling in, flying), which itself never changes; nothing around it moves.
// Reduced motion: no acts and no loops, the mood's end pose. Below 40 px, before the text arrives, or if the drawing
// is not the rig's shape, the plain drawing keeps the CSS moves of cast.css; if the file fails, the Pulse stands in.
import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { prefersReducedMotion } from '@/motion/reduced';
import { uniqueIds } from './rig';
import { attachPuppet, type Puppet } from './puppet';
import { defaultAct, type ActName } from './acts';
import { Pulse, type PulseMood } from './Pulse';
import { castSrc, isCastName, DEFAULT_CAST } from './cast';
import './cast.css';

export type CastMood = 'right' | 'wrong' | 'partial' | 'none' | 'podium' | 'idle';

/** The Pulse's mood for each cast mood, when the drawing cannot be shown. */
export const CAST_FALLBACK_MOOD: Readonly<Record<CastMood, PulseMood>> = {
  right: 'spike', wrong: 'jolt', partial: 'steady', none: 'flat', podium: 'crown', idle: 'steady',
};

/** Below this size the simplified glyph is drawn. */
export const GLYPH_BELOW = 40;
/** How long the plain drawing waits, hidden, for the rig's text before showing as it is. */
export const RIG_WAIT_MS = 1500;

export interface CastMascotProps {
  name: string;
  mood: CastMood;
  size: number;
  className?: string;
  /** What a screen reader hears; without it the mascot is decorative. */
  label?: string;
  /** The act to open with; null for none (life and the loop only); left out, the mood's first pick. */
  act?: ActName | null;
  /** Hold the first act's opening frame this long, in ms (a parade's staggered drop-in). */
  delay?: number;
  /** Play another act now; a new `n` plays again. */
  cue?: { act: ActName; n: number };
}

const texts = new Map<string, Promise<string | undefined>>();
const known = new Map<string, string | undefined>();
/** The drawing's text, fetched once per page; undefined where it cannot be fetched (tests, offline, not an SVG). */
export function castText(name: string): Promise<string | undefined> {
  let t = texts.get(name);
  if (!t) {
    t = (async () => {
      try {
        if (typeof fetch !== 'function' || typeof location === 'undefined' || !/^https?:/.test(location.protocol)) return undefined;
        const res = await fetch(castSrc(name));
        const text = res.ok ? await res.text() : '';
        return /^\s*<svg[\s>]/.test(text) && !/<script|\son\w+=/i.test(text) ? text : undefined;
      } catch { return undefined; }
    })().then((v) => { known.set(name, v); return v; });
    texts.set(name, t);
  }
  return t;
}
/** Starts fetching a drawing early (the question screen fetches its reveal's character). */
export const preloadCast = (name: string) => { void castText(name); };

/** The drawing inline, alive. */
function Puppeteer({ cast, text, mood, size, act, delay, cue, onPosed }: {
  cast: string; text: string; mood: CastMood; size: number; act?: ActName | null; delay?: number; cue?: CastMascotProps['cue']; onPosed: (ok: boolean) => void;
}) {
  const holder = useRef<HTMLSpanElement>(null);
  const wrap = useRef<HTMLSpanElement>(null);
  const hand = useRef<HTMLSpanElement>(null);
  const burst = useRef<HTMLSpanElement>(null);
  const shadow = useRef<HTMLSpanElement>(null);
  const puppet = useRef<Puppet>(undefined);
  const played = useRef<string>('');
  const cued = useRef<number | undefined>(cue?.n);
  const uid = `c${useId().replace(/[^A-Za-z0-9]/g, '')}`;
  const [html] = useState(() => uniqueIds(text, uid).replace(/\swidth="\d+"\s+height="\d+"/, ` width="${size}" height="${size}"`));
  const chosen = act === undefined ? defaultAct(mood, cast) : act;

  useLayoutEffect(() => {
    const svg = holder.current?.querySelector('svg');
    if (!svg || !wrap.current) { onPosed(false); return; }
    svg.setAttribute('width', String(size)); svg.setAttribute('height', String(size));
    const p = attachPuppet({ svg, wrap: wrap.current, hand: hand.current, burst: burst.current, shadow: shadow.current, size, beat: cast === 'beat', reduced: prefersReducedMotion() });
    if (!p) { onPosed(false); return; }
    onPosed(true);
    puppet.current = p;
    played.current = `${mood}|${chosen}`;
    p.play(mood, chosen, delay ?? 0);
    return () => { p.destroy(); puppet.current = undefined; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- one puppet per drawing (keyed by cast and size)

  useEffect(() => {
    const k = `${mood}|${chosen}`;
    if (!puppet.current || played.current === k) return;
    played.current = k;
    puppet.current.play(mood, chosen);
  }, [mood, chosen]);

  useEffect(() => {
    if (!cue || cued.current === cue.n) return;
    cued.current = cue.n;
    puppet.current?.play(mood, cue.act);
  }, [cue?.n]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
    <span ref={shadow} className="rd-cast-shadow" />
    <span ref={wrap} className="rd-cast-body" style={{ width: size, height: size }}>
      <span ref={holder} className="rd-cast-rig" style={{ width: size, height: size, display: 'block' }} dangerouslySetInnerHTML={{ __html: html }} />
      {mood === 'podium' && <Crown size={size} />}
      <span ref={hand} className="rd-cast-hand" style={{ width: size * 0.4, height: size * 0.4 }}><FriendHand /></span>
      <span ref={burst} className="rd-cast-burst">
        {BURST.map((c, i) => (
          <svg key={i} viewBox="0 0 20 20" width={Math.max(8, size * 0.15)} height={Math.max(8, size * 0.15)} aria-hidden="true">
            <path d="M10 0 C11 7 13 9 20 10 C13 11 11 13 10 20 C9 13 7 11 0 10 C7 9 9 7 10 0 Z" fill={c} />
          </svg>
        ))}
      </span>
    </span>
    </>
  );
}
const BURST = ['#ffc800', '#58cc02', '#7c3aed', '#ffc800', '#ff9600', '#58cc02', '#1cb0f6'];

export function CastMascot({ name, mood, size, className = '', label, act, delay, cue }: CastMascotProps) {
  const cast = isCastName(name) ? name : DEFAULT_CAST;
  const glyph = size < GLYPH_BELOW;
  const [failed, setFailed] = useState(false);
  const [text, setText] = useState<{ cast: string; text: string } | undefined>(() => {
    const t = known.get(cast);
    return t ? { cast, text: t } : undefined;
  });
  const [plain, setPlain] = useState<string>();     // the cast shown plain: no text, timed out, or not the rig's shape
  const [rigged, setRigged] = useState<boolean>();
  const [seen, setSeen] = useState(cast);
  if (seen !== cast) { setSeen(cast); setFailed(false); setRigged(undefined); } // a new character starts afresh
  useEffect(() => {
    if (glyph) return;
    let live = true;
    void castText(cast).then((t) => { if (!live) return; if (t) setText({ cast, text: t }); else setPlain(cast); });
    const timer = setTimeout(() => { if (live) setPlain((p) => p ?? cast); }, RIG_WAIT_MS);
    return () => { live = false; clearTimeout(timer); };
  }, [cast, glyph]);
  const wantRig = !glyph && rigged !== false;
  const inline = wantRig && text?.cast === cast;
  const waiting = wantRig && !inline && plain !== cast;
  const a11y = label ? { role: 'img' as const, 'aria-label': label } : { 'aria-hidden': true as const };

  if (failed) {
    return (
      <span data-part="cast-mascot" data-cast={cast} data-mood={mood} data-fallback="pulse" {...a11y}
        className={`relative inline-flex shrink-0 items-center justify-center ${className}`}
        style={{ width: size, height: size, overflow: 'visible' }}>
        <Pulse mood={CAST_FALLBACK_MOOD[mood]} width={size} height={Math.round(size * 0.42)} glow={mood === 'right' || mood === 'podium'} />
      </span>
    );
  }

  return (
    <span data-part="cast-mascot" data-cast={cast} data-mood={mood} data-act={(act === undefined ? defaultAct(mood, cast) : act) ?? undefined}
      data-rig={inline && rigged ? '' : undefined} {...a11y}
      className={`rd-cast relative inline-flex shrink-0 items-end justify-center ${className}`}
      style={{ width: size, height: size, overflow: 'visible' }}>
      {inline
        ? <Puppeteer key={`${cast}-${size}`} cast={cast} text={text!.text} mood={mood} size={size} act={act} delay={delay} cue={cue} onPosed={setRigged} />
        : (
          <span key={waiting ? 'wait' : 'plain'} className="rd-cast-body" style={{ width: size, height: size, visibility: waiting ? 'hidden' : undefined }}>
            <img src={castSrc(cast, glyph)} alt="" width={size} height={size} draggable={false}
              decoding="async" onError={() => setFailed(true)}
              style={{ width: size, height: size, display: 'block' }} />
          </span>
        )}
      {!inline && mood === 'podium' && <Crown size={size} />}
      {!inline && !waiting && mood === 'right' && <Sparks size={size} />}
      {!inline && !waiting && mood === 'none' && <span className="rd-cast-z" aria-hidden="true" style={{ fontSize: Math.max(10, size * 0.16) }}>z</span>}
    </span>
  );
}

/** A small three-point crown, ours, above the character. */
function Crown({ size }: { size: number }) {
  const w = size * 0.46;
  return (
    <svg className="rd-cast-crown" viewBox="0 0 40 26" width={w} height={w * 0.65} aria-hidden="true"
      style={{ top: -w * 0.42 }}>
      <path d="M3 23 L3 8 L12 15 L20 3 L28 15 L37 8 L37 23 Z" fill="#ffc800" stroke="#e5a000" strokeWidth="2.4" strokeLinejoin="round" />
      <rect x="3" y="19" width="34" height="4" fill="#e5a000" />
      <circle cx="20" cy="3.2" r="2.6" fill="#ff4b4b" />
      <circle cx="3" cy="8" r="2.2" fill="#7c3aed" />
      <circle cx="37" cy="8" r="2.2" fill="#7c3aed" />
    </svg>
  );
}

/** A friend's hand, ours, in the cast's limb shape: a capsule arm and a round palm, coming in from the right. */
function FriendHand() {
  return (
    <svg viewBox="0 0 40 40" width="100%" height="100%" aria-hidden="true">
      <rect x="18" y="20" width="26" height="11" rx="5.5" fill="#7c4fe0" />
      <circle cx="16" cy="22" r="10" fill="#9265f2" />
      <rect x="5" y="9" width="5" height="12" rx="2.5" fill="#9265f2" transform="rotate(-18 7.5 15)" />
      <rect x="11" y="6" width="5" height="12" rx="2.5" fill="#9265f2" transform="rotate(-6 13.5 12)" />
      <rect x="17" y="7" width="5" height="11" rx="2.5" fill="#9265f2" transform="rotate(8 19.5 12)" />
    </svg>
  );
}

const SPARKS: ReadonlyArray<[number, number, number, number]> = [
  // x %, y %, scale, delay ms
  [8, 18, 1, 120], [88, 12, 0.8, 200], [96, 58, 0.6, 260], [2, 62, 0.7, 300], [50, -8, 0.9, 160],
];

/** A short burst of four-point sparks around the plain drawing (the right answer). */
function Sparks({ size }: { size: number }) {
  const s = Math.max(8, size * 0.16);
  return (
    <>
      {SPARKS.map(([x, y, k, d], i) => (
        <svg key={i} className="rd-cast-spark" viewBox="0 0 20 20" width={s * k} height={s * k} aria-hidden="true"
          style={{ left: `${x}%`, top: `${y}%`, animationDelay: `${d}ms` }}>
          <path d="M10 0 C11 7 13 9 20 10 C13 11 11 13 10 20 C9 13 7 11 0 10 C7 9 9 7 10 0 Z" fill={i % 2 ? '#ffc800' : '#58cc02'} />
        </svg>
      ))}
    </>
  );
}
